import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";

/**
 * One-line confirmations: archived, sent, saved to drafts.
 *
 * Deliberately not a stack. Everything that speaks through here is the receipt
 * for an action the operator just took, and by the time a second one arrives the
 * first is no longer news - so a new message replaces the old one rather than
 * piling up in the corner.
 */
const ToastContext = createContext<(message: ReactNode) => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastHost({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<ReactNode>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toast = useCallback((next: ReactNode) => {
    setMessage(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), 2800);
  }, []);

  const value = useMemo(() => toast, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <AnimatePresence>
          {message ? (
            <motion.div
              className="crm-toast"
              role="status"
              aria-live="polite"
              initial={{ opacity: 0, y: 14, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: 10, x: "-50%" }}
              transition={{ duration: 0.28, ease: [0.25, 1, 0.5, 1] }}
            >
              <Check className="size-4" />
              {message}
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
